import type { CareStateEvent, CareStateRow, CareStateUpdate } from '../model/types.js';
import type { UpdateReply } from '../model/index.js';
import type {
  CareStateAcceptedUpdateReplyT,
  CareStateCreateRowOperationTRow,
  CareStateDeleteRowOperationT,
  CareStateRejectedUpdateReplyT,
  CareStateReplaceEventT,
  CareStateReplaceEventTNewValue,
  CareStateReplaceEventTOldValue,
  CareStateUpdateRowOperationT,
  CareStateUpdateRowOperationTNewValue,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { CareStateUpdateDto } from './api.js';

/**
 * Codecs translate between:
 * - DTO types (server/wire shapes; snake_case; minimally typed JSON)
 * - canonical model types (app domain; camelCase; typed per DbT)

 */

export function decodeCareStateEvent<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  event: CareStateReplaceEventT
): CareStateEvent<DbT> {
  return {
    type: 'replace',
    table: event.table as Extract<keyof DbT, string>,
    id: event.id,
    oldValue: event.old_value as unknown as DbT[Extract<keyof DbT, string>][number] | null,
    newValue: event.new_value as unknown as DbT[Extract<keyof DbT, string>][number] | null,
  } as CareStateEvent<DbT>;
}

export function encodeCareStateEvent<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  event: CareStateEvent<DbT>
): CareStateReplaceEventT {
  return {
    type: 'replace',
    table: String(event.table),
    id: event.id,
    old_value: event.oldValue as unknown as CareStateReplaceEventTOldValue,
    new_value: event.newValue as unknown as CareStateReplaceEventTNewValue,
  };
}

// == updates ==

export function encodeCareStateUpdate<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  update: CareStateUpdate<DbT>
): CareStateUpdateDto {
  switch (update.type) {
    case 'create':
      return {
        type: 'create',
        table: String(update.table),
        row: update.row as unknown as CareStateCreateRowOperationTRow,
      };
    case 'update':
      return {
        type: 'update',
        table: String(update.table),
        id: update.row.id,
        new_value: update.row as unknown as CareStateUpdateRowOperationTNewValue,
        conditional_old_value:
          update.conditionalOldValue as unknown as CareStateUpdateRowOperationT['conditional_old_value'],
      };
    case 'delete':
      return {
        type: 'delete',
        table: String(update.table),
        id: update.id,
        conditional_old_value:
          update.conditionalOldValue as unknown as CareStateDeleteRowOperationT['conditional_old_value'],
      };
    default: {
      const _exhaustive: never = update;
      void _exhaustive;
      throw new Error('Unknown CareState update');
    }
  }
}

export function decodeCareStateUpdate<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  update: CareStateUpdateDto
): CareStateUpdate<DbT> {
  switch (update.type) {
    case 'create':
      return {
        type: 'create',
        table: update.table as keyof DbT,
        row: update.row as unknown as DbT[keyof DbT][number],
      } as CareStateUpdate<DbT>;
    case 'update': {
      const row = { ...(update.new_value as unknown as Record<string, unknown>), id: update.id };
      return {
        type: 'update',
        table: update.table as keyof DbT,
        row: row as unknown as DbT[keyof DbT][number],
        conditionalOldValue: update.conditional_old_value as unknown as
          | DbT[keyof DbT][number]
          | null,
      } as CareStateUpdate<DbT>;
    }
    case 'delete':
      return {
        type: 'delete',
        table: update.table as keyof DbT,
        id: update.id,
        conditionalOldValue: update.conditional_old_value as unknown as
          | DbT[keyof DbT][number]
          | null,
      } as CareStateUpdate<DbT>;
    default: {
      throw new Error('Unknown CareState update DTO');
    }
  }
}

// == replies ==

export function decodeCareStateUpdateReply(
  reply: CareStateAcceptedUpdateReplyT | CareStateRejectedUpdateReplyT
): UpdateReply {
  switch (reply.type) {
    case 'acceptedUpdate':
      return { type: 'acceptedUpdate', cursor: reply.cursor };
    case 'rejectedUpdate':
      return { type: 'rejectedUpdate', reason: reply.reason };
    default: {
      // SDK schema types may model discriminators as optional (e.g. `type?: "acceptedUpdate"`).
      throw new Error('Unknown CareState update reply');
    }
  }
}

export function encodeCareStateUpdateReply(
  reply: UpdateReply
): CareStateAcceptedUpdateReplyT | CareStateRejectedUpdateReplyT {
  switch (reply.type) {
    case 'acceptedUpdate':
      return { type: 'acceptedUpdate', cursor: reply.cursor };
    case 'rejectedUpdate':
      return { type: 'rejectedUpdate', reason: reply.reason };
    default: {
      const _exhaustive: never = reply;
      void _exhaustive;
      throw new Error('Unknown CareState update reply');
    }
  }
}
